import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { api, formatApiError } from "@/lib/api";
import { img, REGIONS } from "@/lib/content";

const SUBJECTS = ["General Enquiry", "Employer — New Mandate", "Candidate Enquiry", "Partnership", "Media & Press"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "General Enquiry", message: "", website: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/contact", { ...form, source_page: "contact" });
      setReference(r.data.reference);
      toast.success("Message received.");
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact — NEXREQ UAE & GCC"
        siteName="NEXREQ"
        description="Contact NEXREQ UAE & GCC — Dubai office, email and enquiry form for employers and professionals across the GCC."
      />
      <PageHero
        eyebrow="Contact"
        lines={["Start the", "Conversation."]}
        description="Whether you are hiring, exploring your next role or simply want market perspective — our team responds within one business day."
        image={img("photo-1512453979798-5ea266f8880c", 1920)}
      />

      <section className="bg-obsidian pb-28" data-testid="contact-section">
        <div className="nx-container grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-10">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">Dubai Office</p>
                  <p className="mt-3 leading-relaxed text-white/65">
                    Bin Dasmal Building – 1<br />
                    Al Quoz Industrial First<br />
                    Dubai, United Arab Emirates
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">Email</p>
                  <a href="mailto:info@nexreq.ae" className="mt-3 block text-lg text-emerald-400 hover:underline" data-testid="contact-email-link">
                    info@nexreq.ae
                  </a>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">Website</p>
                  <a href="https://www.nexreq.ae" target="_blank" rel="noreferrer" className="mt-3 block text-lg text-white/70 hover:text-emerald-400" data-testid="contact-website-link">
                    www.nexreq.ae
                  </a>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">We Serve</p>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                    {REGIONS.map((r) => (
                      <span key={r} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                        <span className="h-1 w-1 rounded-full bg-emerald-600" />
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {reference ? (
              <Reveal>
                <div className="border border-white/10 bg-surface p-10 lg:p-14" data-testid="contact-success">
                  <span className="flex h-12 w-12 items-center justify-center bg-emerald-600/15 text-emerald-400">
                    <Check size={22} />
                  </span>
                  <h2 className="mt-7 font-heading text-3xl font-light text-white">Message Received.</h2>
                  <p className="mt-5 leading-relaxed text-white/55">
                    Thank you for contacting NEXREQ. Your enquiry has been received. A member of our
                    UAE &amp; GCC team will review your request and contact you shortly.
                  </p>
                  <p className="mt-7 inline-block border border-white/10 bg-obsidian px-5 py-3 font-mono text-xs tracking-[0.15em] text-emerald-400">
                    REF: {reference}
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={submit} className="border border-white/10 bg-surface p-8 lg:p-12" data-testid="contact-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="nx-label" htmlFor="ct-name">Full Name *</label>
                      <input id="ct-name" className="nx-input" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="nx-label" htmlFor="ct-email">Email *</label>
                      <input id="ct-email" type="email" className="nx-input" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                    </div>
                    <div>
                      <label className="nx-label" htmlFor="ct-phone">Phone</label>
                      <input id="ct-phone" className="nx-input" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                    </div>
                    <div>
                      <label className="nx-label" htmlFor="ct-company">Company</label>
                      <input id="ct-company" className="nx-input" value={form.company} onChange={set("company")} data-testid="contact-company-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="nx-label" htmlFor="ct-subject">Subject</label>
                      <select id="ct-subject" className="nx-input cursor-pointer appearance-none [&>option]:bg-obsidian" value={form.subject} onChange={set("subject")} data-testid="contact-subject-select">
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="nx-label" htmlFor="ct-message">Message *</label>
                      <textarea id="ct-message" rows={5} className="nx-input" required value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                    </div>
                  </div>
                  <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                  <label className="mt-7 flex items-start gap-3 text-xs leading-relaxed text-white/50" data-testid="contact-consent-label">
                    <input type="checkbox" required checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" data-testid="contact-consent-checkbox" />
                    <span>I consent to NEXREQ processing this information to respond to my enquiry, as described in the <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>.</span>
                  </label>
                  <button type="submit" className="nx-btn-primary mt-7 w-full sm:w-auto" disabled={submitting} data-testid="contact-submit-btn">
                    {submitting ? "Sending…" : "Send Message"} <ArrowRight size={15} />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
